import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./hooks/useAuth";
import { useLocalStorageSync } from "./hooks/useLocalStorageSync";
import Layout from "./components/Layout";

// Lazy load pages for better code splitting
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const EvaluatePage = lazy(() => import("./pages/EvaluatePage"));
const EmployeeDetailPage = lazy(() => import("./pages/EmployeeDetailPage"));
const EvaluationMatrixPage = lazy(() => import("./pages/EvaluationMatrixPage"));
const AuthPage = lazy(() => import("./pages/AuthPage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const EmployeeListPage = lazy(() => import("./pages/EmployeeListPage"));
const OrgChartPage = lazy(() => import("./pages/OrgChartPage"));
const ApplicantManagementPage = lazy(() => import("./pages/ApplicantManagementPage"));
const ApplicantDetailPage = lazy(() => import("./pages/ApplicantDetailPage"));
const ApplyPage = lazy(() => import("./pages/ApplyPage"));
const CostOverviewPage = lazy(() => import("./pages/CostOverviewPage"));
const HelpPage = lazy(() => import("./pages/HelpPage"));
const EmployeeDirectoryPage = lazy(() => import("./pages/EmployeeDirectoryPage"));
const WorkProcessesPage = lazy(() => import("./pages/WorkProcessesPage"));
const VehicleUploadsOverviewPage = lazy(() => import("./pages/VehicleUploadsOverviewPage"));
const DocumentPreviewPage = lazy(() => import("./pages/DocumentPreviewPage"));
const SurveyPage = lazy(() => import("./pages/SurveyPage"));
const InterviewResponsePage = lazy(() => import("./pages/InterviewResponsePage"));

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="text-muted-foreground">Wird geladen...</div>
  </div>
);

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-muted-foreground">Wird geladen...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  return <Layout>{children}</Layout>;
};

// Protected route without Layout wrapper (for fullscreen pages like document preview)
const ProtectedRouteNoLayout = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-muted-foreground">Wird geladen...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  return <>{children}</>;
};

const queryClient = new QueryClient();

const App = () => {
  useLocalStorageSync();
  
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <Suspense fallback={<PageLoader />}>
            <main className="min-h-screen">
              <Routes>
                <Route path="/auth" element={<AuthPage />} />
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <DashboardPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/employees"
                  element={
                    <ProtectedRoute>
                      <EmployeeListPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/orgchart"
                  element={
                    <ProtectedRoute>
                      <OrgChartPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/matrix"
                  element={
                    <ProtectedRoute>
                      <EvaluationMatrixPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <ProtectedRoute>
                      <SettingsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/evaluate/:employeeId"
                  element={
                    <ProtectedRoute>
                      <EvaluatePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/employee/:employeeId"
                  element={
                    <ProtectedRoute>
                      <EmployeeDetailPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/applicants"
                  element={
                    <ProtectedRoute>
                      <ApplicantManagementPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/applicants/:id"
                  element={
                    <ProtectedRoute>
                      <ApplicantDetailPage />
                    </ProtectedRoute>
                  }
                />
                <Route path="/apply" element={<ApplyPage />} />
                <Route path="/interview-response/:token" element={<InterviewResponsePage />} />
                <Route
                  path="/cost-overview"
                  element={
                    <ProtectedRoute>
                      <CostOverviewPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/directory"
                  element={
                    <ProtectedRoute>
                      <EmployeeDirectoryPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/work-processes"
                  element={
                    <ProtectedRoute>
                      <WorkProcessesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/vehicle-uploads"
                  element={
                    <ProtectedRoute>
                      <VehicleUploadsOverviewPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/help"
                  element={
                    <ProtectedRoute>
                      <HelpPage />
                    </ProtectedRoute>
                  }
                />
                {/* Survey - for all authenticated users */}
                <Route
                  path="/survey"
                  element={
                    <ProtectedRoute>
                      <SurveyPage />
                    </ProtectedRoute>
                  }
                />
                {/* Document Preview - fullscreen without sidebar/navigation */}
                <Route
                  path="/preview"
                  element={
                    <ProtectedRouteNoLayout>
                      <DocumentPreviewPage />
                    </ProtectedRouteNoLayout>
                  }
                />
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </main>
          </Suspense>
          </BrowserRouter>
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
};

export default App;
